import { startTransition, useCallback, useEffect, useState } from "react";
import { getSupabase } from "@dwelldrop/supabase";
import { useVenue } from "../contexts/VenueContext";
import type { Notification } from "../types/notification";
import {
  requestBrowserNotificationPermission,
  showBrowserNotification,
} from "../lib/browserNotifications";

interface RpcErrorLike {
  code?: string;
  details?: string | null;
  message?: string | null;
}

function isMissingRpcFunction(
  error: RpcErrorLike | null,
  functionName: string
): boolean {
  if (!error) {
    return false;
  }

  if (error.code === "PGRST202") {
    return true;
  }

  return `${error.message ?? ""} ${error.details ?? ""}`.includes(functionName);
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  deleteNotifications: (notificationIds: string[]) => Promise<boolean>;
  clearAllNotifications: () => Promise<boolean>;
}

export function useNotifications(): UseNotificationsReturn {
  const { currentVenue } = useVenue();
  const venueId = currentVenue?.id ?? null;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const supabase = getSupabase();
      const { data, error: rpcError } = await supabase.rpc(
        "get_my_notifications",
        {
          p_unread_only: false,
          p_venue_id: venueId,
        }
      );

      if (rpcError) {
        setError("Failed to load notifications");
        return;
      }

      startTransition(() => {
        setNotifications((data as Notification[]) || []);
      });
    } catch {
      setError("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  const markAsRead = useCallback(async (notificationIds: string[]) => {
    if (notificationIds.length === 0) return;

    try {
      const supabase = getSupabase();
      const { error: rpcError } = await supabase.rpc(
        "mark_notifications_read",
        {
          p_ids: notificationIds,
        }
      );

      if (!rpcError) {
        startTransition(() => {
          setNotifications((prev) =>
            prev.map((n) =>
              notificationIds.includes(n.id) ? { ...n, read: true } : n
            )
          );
        });
      }
    } catch {
      // Silently fail
    }
  }, []);

  const deleteNotifications = useCallback(
    async (notificationIds: string[]) => {
      if (notificationIds.length === 0) return true;

      const previousNotifications = notifications;

      startTransition(() => {
        setNotifications((prev) =>
          prev.filter(
            (notification) => !notificationIds.includes(notification.id)
          )
        );
      });

      try {
        const supabase = getSupabase();
        const { error: rpcError } = await supabase.rpc(
          "delete_my_notifications",
          {
            p_ids: notificationIds,
          }
        );

        if (
          rpcError &&
          isMissingRpcFunction(rpcError, "delete_my_notifications")
        ) {
          const { error: deleteError } = await supabase
            .from("notifications")
            .update({ deleted_at: new Date().toISOString() })
            .in("id", notificationIds);

          if (deleteError) {
            startTransition(() => {
              setNotifications(previousNotifications);
            });
            return false;
          }
        } else if (rpcError) {
          startTransition(() => {
            setNotifications(previousNotifications);
          });
          return false;
        }

        return true;
      } catch {
        startTransition(() => {
          setNotifications(previousNotifications);
        });
        return false;
      }
    },
    [notifications]
  );

  const clearAllNotifications = useCallback(async () => {
    if (notifications.length === 0) return true;

    const previousNotifications = notifications;

    startTransition(() => {
      setNotifications([]);
    });

    try {
      const supabase = getSupabase();
      const { error: rpcError } = await supabase.rpc("clear_my_notifications", {
        p_venue_id: venueId,
      });

      if (
        rpcError &&
        isMissingRpcFunction(rpcError, "clear_my_notifications")
      ) {
        const { error: deleteError } = await supabase
          .from("notifications")
          .update({ deleted_at: new Date().toISOString() })
          .not("id", "is", null);

        if (deleteError) {
          startTransition(() => {
            setNotifications(previousNotifications);
          });
          return false;
        }
      } else if (rpcError) {
        startTransition(() => {
          setNotifications(previousNotifications);
        });
        return false;
      }

      return true;
    } catch {
      startTransition(() => {
        setNotifications(previousNotifications);
      });
      return false;
    }
  }, [notifications, venueId]);

  useEffect(() => {
    fetchNotifications();

    const supabase = getSupabase();
    const channel = supabase
      .channel("notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
        },
        (payload) => {
          fetchNotifications();
          const notification = payload.new as Notification;
          // Only alert for this venue or owner-level notifications
          if (!notification.venue_id || notification.venue_id === venueId) {
            showBrowserNotification(notification);
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
        },
        () => {
          fetchNotifications();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "notifications",
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [fetchNotifications, venueId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void fetchNotifications();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    requestBrowserNotificationPermission();
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refetch: fetchNotifications,
    markAsRead,
    deleteNotifications,
    clearAllNotifications,
  };
}
